// src/encounter/lib/booked-specialty.ts
//
// THE VISIT'S BOOKED SPECIALTY — the encounter-template `specialtyId` axis (PROD-1162, refined by
// PROD-1496, made reachable on deep links by PROD-2701).
//
// ONE RULE, TWO CALLERS. The appointment list builds router state from a tapped row
// (`toEncounterNav`), and the encounter page reads the same rule off its own GetById fetch
// (`useAppointmentStatus`) when it was opened WITHOUT router state (pasted link, new tab,
// bookmark). Both go through `bookedSpecialtyId` so the two paths cannot drift: since PROD-2696
// QuestionBank ranks templates by specialty, so a disagreement here is a different template.
//
// THE ORDER IS LOAD-BEARING. The appointment's own booked specialty comes first, the provider
// participant's speciality only as the fallback. On real dev data a multi-specialty provider's
// participant node carries their PRIMARY specialty (e.g. Dietician) while the appointment was
// booked as General practice — template selection must follow the visit, not the provider.
//
// Wire spellings: British `speciality` first, then American `specialty` (the Scheduling
// `AppointmentResponse` serialises `Specialty` → `specialty: { specialitySkillId, code }`), then
// the bare `specialityId` GUID. The lean wire types do not model these fields (name clash with
// scheduling-api's `{code,display}` Speciality), so both reads go through narrow views.

type SpecialtyView = {
  speciality?: { specialitySkillId?: unknown } | null;
  specialty?: { specialitySkillId?: unknown } | null;
  specialityId?: unknown;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Only a non-empty STRING is a specialty. A malformed axis (number/object) would be coerced by
 * URLSearchParams and 400 the whole template fetch; the all-zero GUID is what the backend's
 * non-nullable `Guid SpecialityId` serialises when nothing was booked, and sending it would filter
 * templates by a specialty that does not exist.
 */
function asSpecialtyId(v: unknown): string | undefined {
  return typeof v === 'string' && v && v !== EMPTY_GUID ? v : undefined;
}

/**
 * The booked specialty of an appointment, or undefined when none can be read.
 *
 * @param appointment the appointment record (a list row or the GetById payload).
 * @param provider    the provider participant's node, the fallback source.
 */
export function bookedSpecialtyId(appointment: unknown, provider?: unknown): string | undefined {
  const a = (appointment ?? {}) as SpecialtyView;
  const p = (provider ?? {}) as SpecialtyView;
  return (
    asSpecialtyId(a.speciality?.specialitySkillId) ??
    asSpecialtyId(a.specialty?.specialitySkillId) ??
    asSpecialtyId(a.specialityId) ??
    asSpecialtyId(p.speciality?.specialitySkillId) ??
    asSpecialtyId(p.specialty?.specialitySkillId)
  );
}

type SpecialtyNameView = {
  speciality?: { code?: { displayName?: unknown; display?: unknown } | null; display?: unknown; name?: unknown } | null;
  specialty?: { code?: { displayName?: unknown; display?: unknown } | null; display?: unknown; name?: unknown } | null;
  specialityName?: unknown;
};

function asName(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

function nameOf(node: SpecialtyNameView['speciality']): string | undefined {
  return asName(node?.code?.displayName) ?? asName(node?.code?.display) ?? asName(node?.display) ?? asName(node?.name);
}

/**
 * PROD-2947 · the booked specialty's NAME (Andrew's walk, 7 Oct, PROD-3055 d): the discipline of this visit, for the
 * allied-health plan's framework and the allied phrase editors. Same order as `bookedSpecialtyId`: the appointment's own
 * booked specialty, then the provider participant's. Undefined when none carries a name.
 */
export function bookedSpecialtyName(appointment: unknown, provider?: unknown): string | undefined {
  const a = (appointment ?? {}) as SpecialtyNameView;
  const p = (provider ?? {}) as SpecialtyNameView;
  return nameOf(a.speciality) ?? nameOf(a.specialty) ?? asName(a.specialityName) ?? nameOf(p.speciality) ?? nameOf(p.specialty);
}

/** The template request's specialty, and whether it is still being looked up. */
export interface TemplateSpecialty {
  specialtyId?: string;
  /** True while the fallback lookup is in flight — the template request must wait for it. */
  pending: boolean;
  /**
   * True when the request goes out WITHOUT a specialty because the lookup FAILED (the appointment
   * read errored), not because the visit has none — so the page can say the template was chosen
   * without the visit's specialty (contract G11) instead of passing that silently.
   */
  lookupFailed?: boolean;
}

/**
 * Which specialty the encounter-template request carries.
 *
 *  - Router state has one (the list path) → use it, never wait for anything.
 *  - Otherwise, the page's own appointment fetch: wait while it is loading (PROD-2608 pins the
 *    FIRST template picked, so firing unconstrained and correcting later would stick); once it
 *    settles, use its specialty — or, if it failed or had none, send no specialty (the behaviour
 *    before PROD-2701), never an error. A FAILED read is flagged (`lookupFailed`) so the page can
 *    say the template was chosen without the visit's specialty (G11).
 *
 * `isLoading` (TanStack: first fetch in flight, retries included) is what holds. A disabled query
 * (no appointment id) is not loading, so it never holds.
 *
 * KNOWN TRADE-OFF: if GetById fails, the hold lasts through TanStack's default retries (~7s)
 * before the template goes out without a specialty. Deliberate: the status pill and room read
 * the same query, so its retry policy is not this rule's to change.
 */
export function resolveTemplateSpecialty(
  navSpecialtyId: string | undefined,
  appointment: { data?: { specialtyId?: string | null } | null; isLoading?: boolean; isError?: boolean },
): TemplateSpecialty {
  const fromNav = asSpecialtyId(navSpecialtyId);
  if (fromNav) return { specialtyId: fromNav, pending: false };
  if (appointment.isLoading) return { pending: true };
  const fromAppointment = asSpecialtyId(appointment.data?.specialtyId);
  if (fromAppointment) return { specialtyId: fromAppointment, pending: false };
  // A failed read is not "no specialty booked" (R6): say which it was.
  return appointment.isError ? { pending: false, lookupFailed: true } : { pending: false };
}
